import { useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import { wrapMarkdownBold, wrapMarkdownItalic } from "../utils/markdownBoldWrap.js"

interface Block {
  title: string;
  description: string;
}

interface TitleDescriptionBlocksModalProps {
  open: boolean;
  onClose: () => void;
  sectionLabel: string;
  blocks: Block[];
  hideTitle?: boolean;
  onSave: (blocks: Block[]) => void;
}

export default function TitleDescriptionBlocksModal({
  open,
  onClose,
  sectionLabel,
  blocks,
  hideTitle = false,
  onSave,
}: TitleDescriptionBlocksModalProps) {
  const [items, setItems] = useState<Block[]>([])
  const taRefs = useRef<Record<string, HTMLTextAreaElement>>({})

  useEffect(() => {
    if (open && Array.isArray(blocks)) {
      setItems(blocks.map((b) => ({ title: b.title || "", description: b.description || "" })))
      taRefs.current = {}
    }
  }, [open, blocks])

  const updateItem = (index: number, field: "title" | "description", value: string) => {
    setItems((prev) => {
      const copy = [...prev]
      copy[index] = { ...copy[index], [field]: value }
      return copy
    })
  }

  const applyFormat = (index: number, field: "title" | "description", fn: (text: string, start: number, end: number) => { nextText: string; selectionStart: number; selectionEnd: number }) => {
    const key = `${index}-${field}`
    const el = taRefs.current[key]
    if (!el) return
    const text = items[index]?.[field] ?? ""
    const { nextText, selectionStart, selectionEnd } = fn(text, el.selectionStart, el.selectionEnd)
    updateItem(index, field, nextText)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(selectionStart, selectionEnd)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, index: number, field: "title" | "description") => {
    if ((e.metaKey || e.ctrlKey) && e.key === "b") {
      e.preventDefault()
      applyFormat(index, field, wrapMarkdownBold)
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "i") {
      e.preventDefault()
      applyFormat(index, field, wrapMarkdownItalic)
    }
  }

  const renderPreview = () => (
    <div className="border border-gray-200 rounded-md p-3 bg-white text-sm prose prose-sm max-w-none">
      {items.map((item, i) => (
        <div key={i} className={i > 0 ? "mt-3 pt-3 border-t border-gray-100" : ""}>
          {!hideTitle && item.title && <p className="font-semibold text-gray-900 mb-1">{item.title}</p>}
          <ReactMarkdown
            allowedElements={["p", "strong", "em", "br", "ul", "ol", "li"]}
            unwrapDisallowed
            skipHtml
          >
            {item.description || " "}
          </ReactMarkdown>
        </div>
      ))}
    </div>
  )

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 border-0 cursor-default"
        aria-label="Close dialog backdrop"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full mx-8 max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Edit {sectionLabel}</h3>
          <p className="text-xs text-gray-500 mt-2">
            <code>**bold**</code> / <code>*italic*</code> —{" "}
            <kbd className="px-1 bg-gray-100 rounded">Ctrl</kbd>+<kbd className="px-1 bg-gray-100 rounded">B</kbd>{" "}
            /{" "}
            <kbd className="px-1 bg-gray-100 rounded">Ctrl</kbd>+<kbd className="px-1 bg-gray-100 rounded">I</kbd>
          </p>
        </div>
        <div className="px-5 py-3 flex-1 overflow-y-auto flex flex-col gap-4 min-h-0">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">No items to edit.</p>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                {items.map((item, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
                    {!hideTitle && (
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => updateItem(index, "title", e.target.value)}
                        placeholder="Title"
                        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                    <div className="flex gap-2 mb-1 mt-1">
                      <button
                        type="button"
                        onClick={() => applyFormat(index, "description", wrapMarkdownBold)}
                        className="px-3 py-1.5 text-sm font-medium bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                      >
                        Bold
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormat(index, "description", wrapMarkdownItalic)}
                        className="px-3 py-1.5 text-sm italic bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                      >
                        Italic
                      </button>
                    </div>
                    <textarea
                      ref={(el) => {
                        if (el) {
                          taRefs.current[`${index}-description`] = el
                          el.style.height = "auto"
                          el.style.height = el.scrollHeight + "px"
                        }
                      }}
                      value={item.description}
                      onChange={(e) => {
                        updateItem(index, "description", e.target.value)
                        e.target.style.height = "auto"
                        e.target.style.height = e.target.scrollHeight + "px"
                      }}
                      onKeyDown={(e) => handleKeyDown(e, index, "description")}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono resize-none overflow-hidden"
                      rows={1}
                      spellCheck
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-500 mb-1">Preview</p>
                {renderPreview()}
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(items)
              onClose()
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
